import {Answer} from '../models/answer.model';
import {GameStoreState, startQuestionState} from './game-state.model';

/**
 * A partial-state updater in the NgRx-signals idiom.
 * Every operator in this file returns one of these so it can be applied with `patchState`.
 */
export type GameStateUpdater = (state: GameStoreState) => Partial<GameStoreState>;

/** Sets the current error message (or clears it with `undefined`). */
export function setErrorMessage(error: string | undefined): GameStateUpdater {
  return () => ({error});
}

/** Appends the current question state to the list of finished states. */
export function pushFinishedState(): GameStateUpdater {
  return ({finishedQuestionStates, questionState}) => ({
    finishedQuestionStates: [...finishedQuestionStates, questionState],
  });
}

/** Moves play to the question at the given index and resets the question state. */
export function goToQuestion(index: number): GameStateUpdater {
  return () => ({
    currentQuestionIndex: index,
    questionState: {...startQuestionState},
    showAnswerOnMap: false,
    persistedGuesses: [],
  });
}

/** Marks the current question as correctly answered. */
export function markCorrect(): GameStateUpdater {
  return ({questionState}) => ({
    questionState: {...questionState, feedback: 'correct'},
  });
}

/**
 * Registers a wrong guess: either increments the attempt counter, or, when no
 * attempts remain, marks the question as completed with a final wrong feedback.
 */
export function registerWrongGuess(maxAttempts: number): GameStateUpdater {
  return ({questionState}) => {
    if (questionState.attempt < maxAttempts) {
      return {questionState: {...questionState, feedback: 'wrong', attempt: questionState.attempt + 1}};
    }
    return {questionState: {...questionState, feedback: 'wrong-and-over'}};
  };
}

/** Toggles whether the answer layer is shown on the map. */
export function toggleAnswerOnMap(): GameStateUpdater {
  return ({showAnswerOnMap}) => ({showAnswerOnMap: !showAnswerOnMap});
}

/** Marks that the player has requested a hint for the current question. */
export function markHintAsked(): GameStateUpdater {
  return ({questionState}) => ({questionState: {...questionState, hasHadHint: true}});
}

/**
 * Registers a guess: appends it to the guesses and removes it from the options.
 *
 * Also records a serialisable {@link PersistedGuess} keyed by a stable string
 * feature id (`String(feature.get(featureIdKey))`) so the guess can be restored
 * after a browser refresh, once the map has re-created its features.
 */
export function addGuess(guess: Answer): GameStateUpdater {
  return (state) => {
    const {guesses, answerOptions, persistedGuesses, currentGameQuestions, currentQuestionIndex} = state;
    const featureIdKey = currentGameQuestions[currentQuestionIndex]?.gameSetting.featureIdKey;
    const featureId = featureIdKey != null ? String(guess.feature.get(featureIdKey)) : '';
    return {
      guesses: [...guesses, guess],
      answerOptions: answerOptions.filter(option => option.feature !== guess.feature),
      persistedGuesses: [...persistedGuesses, {label: guess.label, featureId, isCorrect: guess.isCorrect}],
    };
  };
}

/** Replaces the answer options for the current question. */
export function setAnswerOptions(answerOptions: Answer[]): GameStateUpdater {
  return () => ({answerOptions});
}

/** Replaces the guesses for the current question. */
export function setGuesses(guesses: Answer[]): GameStateUpdater {
  return () => ({guesses});
}

/**
 * Restores rehydrated guesses (e.g. after a refresh) without touching the
 * question state: sets the guesses and removes their features from the
 * remaining answer options.
 */
export function restoreGuesses(guesses: Answer[]): GameStateUpdater {
  return ({answerOptions}) => ({
    guesses,
    answerOptions: answerOptions.filter(option => !guesses.some(guess => guess.feature === option.feature)),
  });
}

/** Sets the name of the correct answer feature. */
export function setAnswer(answer: string): GameStateUpdater {
  return () => ({answer});
}

/** Sets the legend url of the currently shown answer layer. */
export function setAnswerLegendUrl(answerLegendUrl: string): GameStateUpdater {
  return () => ({answerLegendUrl});
}